/**
 * Portfolio Context Service
 *
 * Aggregates all portfolio data into a structured format for the Investment Advisor AI.
 * This context is sent with each message to provide personalized advice.
 */

import type { Token, Position, Prices, FIFOResults, CalculatorResult } from '../types';

// ============================================
// Types for Portfolio Context
// ============================================

export interface TokenContext {
  id: string;
  symbol: string;
  displayName: string;
  quantity: number;
  avgCost: number;
  currentPrice: number;
  currentValue: number;
  totalCost: number;
  pl: number;
  plPercent: number;
  allocation: number;
  buyCount: number;
  sellCount: number;
  swapCount: number;
  miningCount: number;
  stakingCount: number;
  firstTransactionDate: string | null;
  lastTransactionDate: string | null;
  hasTargetPrice: boolean;
  targetPrice?: number;
  distanceToTarget?: number; // % to reach target
}

export interface RecentTransaction {
  date: string;
  type: 'buy' | 'sell' | 'swap' | 'mining' | 'staking';
  tokenSymbol: string;
  tokenId: string;
  quantity: number;
  price: number;
  total: number;
}

export interface PortfolioSummary {
  totalValue: number;
  totalCost: number;
  totalPL: number;
  totalPLPercent: number;
  realizedPL: number;
  unrealizedPL: number;
  tokenCount: number;
  positionCount: number;
  profitableTokens: number;
  losingTokens: number;
  bestPerformer: { symbol: string; plPercent: number } | null;
  worstPerformer: { symbol: string; plPercent: number } | null;
}

export interface RiskMetrics {
  largestPosition: string;
  largestPositionPct: number;
  top3Concentration: number;
  concentrationRisk: 'low' | 'medium' | 'high';
  diversificationScore: number; // 0-100
}

export interface PortfolioContext {
  timestamp: string;
  summary: PortfolioSummary;
  tokens: TokenContext[];
  recentTransactions: RecentTransaction[];
  risk: RiskMetrics;
  calculationMethod: 'FIFO' | 'LIFO' | 'AVERAGE_COST';
}

// ============================================
// Helper Functions
// ============================================

function countPositionsByType(positions: Position[]): {
  buy: number;
  sell: number;
  swap: number;
  mining: number;
  staking: number;
} {
  return positions.reduce(
    (acc, pos) => {
      if (pos.is_swap) {
        acc.swap++;
      } else if (pos.type === 'buy') {
        acc.buy++;
      } else if (pos.type === 'sell') {
        acc.sell++;
      } else if (pos.type === 'mining') {
        acc.mining++;
      } else if (pos.type === 'staking') {
        acc.staking++;
      }
      return acc;
    },
    { buy: 0, sell: 0, swap: 0, mining: 0, staking: 0 }
  );
}

function getTransactionDates(positions: Position[]): {
  first: string | null;
  last: string | null;
} {
  if (positions.length === 0) return { first: null, last: null };

  const sorted = [...positions].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
  );

  return {
    first: sorted[0].date,
    last: sorted[sorted.length - 1].date,
  };
}

function extractRecentTransactions(
  tokens: Token[],
  daysBack: number = 30
): RecentTransaction[] {
  const cutoffDate = new Date();
  cutoffDate.setDate(cutoffDate.getDate() - daysBack);

  const transactions: RecentTransaction[] = [];

  for (const token of tokens) {
    for (const pos of token.positions) {
      const posDate = new Date(pos.date);
      if (posDate >= cutoffDate) {
        transactions.push({
          date: pos.date,
          type: pos.is_swap ? 'swap' : pos.type,
          tokenSymbol: token.displayName || token.id.toUpperCase(),
          tokenId: token.id,
          quantity: pos.quantity,
          price: pos.price,
          total: pos.quantity * pos.price,
        });
      }
    }
  }

  // Sort by date descending (most recent first)
  return transactions.sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  );
}

function calculateRiskMetrics(
  tokenContexts: TokenContext[],
  totalValue: number
): RiskMetrics {
  if (tokenContexts.length === 0 || totalValue <= 0) {
    return {
      largestPosition: 'N/A',
      largestPositionPct: 0,
      top3Concentration: 0,
      concentrationRisk: 'low',
      diversificationScore: 0,
    };
  }

  // Sort by allocation descending
  const sorted = [...tokenContexts].sort((a, b) => b.allocation - a.allocation);

  const largestPosition = sorted[0];
  const top3 = sorted.slice(0, 3);
  const top3Concentration = top3.reduce((sum, t) => sum + t.allocation, 0);

  // Concentration risk based on largest position
  let concentrationRisk: 'low' | 'medium' | 'high' = 'low';
  if (largestPosition.allocation > 50) {
    concentrationRisk = 'high';
  } else if (largestPosition.allocation > 30) {
    concentrationRisk = 'medium';
  }

  // Diversification score (higher = better diversified)
  // Based on number of tokens and distribution
  const tokenCount = tokenContexts.length;
  let diversificationScore = 0;

  if (tokenCount >= 10) {
    diversificationScore += 40;
  } else if (tokenCount >= 5) {
    diversificationScore += 25;
  } else if (tokenCount >= 3) {
    diversificationScore += 15;
  }

  // Add points for even distribution (lower concentration = better)
  if (top3Concentration < 50) {
    diversificationScore += 30;
  } else if (top3Concentration < 70) {
    diversificationScore += 20;
  } else if (top3Concentration < 85) {
    diversificationScore += 10;
  }

  // Add points for having multiple profitable tokens
  const profitableRatio =
    tokenContexts.filter((t) => t.pl > 0).length / tokenCount;
  diversificationScore += Math.round(profitableRatio * 30);

  return {
    largestPosition: largestPosition.symbol,
    largestPositionPct: Math.round(largestPosition.allocation * 10) / 10,
    top3Concentration: Math.round(top3Concentration * 10) / 10,
    concentrationRisk,
    diversificationScore: Math.min(100, diversificationScore),
  };
}

// ============================================
// Main Service
// ============================================

export const PortfolioContextService = {
  /**
   * Build the complete portfolio context for the AI advisor
   */
  buildContext(
    tokens: Token[],
    prices: Prices,
    fifoResults: FIFOResults,
    calculationMethod: 'FIFO' | 'LIFO' | 'AVERAGE_COST' = 'FIFO'
  ): PortfolioContext {
    // Calculate total values first
    let totalValue = 0;
    let totalCost = 0;
    let totalRealizedPL = 0;

    const tokenContexts: TokenContext[] = [];

    for (const token of tokens) {
      const fifo = fifoResults[token.id];
      const price = prices[token.id]?.usd || 0;

      if (!fifo) continue;

      const currentValue = fifo.remainingQty * price;
      const unrealizedPL = currentValue - fifo.totalCost;
      const unrealizedPLPct =
        fifo.totalCost > 0 ? (unrealizedPL / fifo.totalCost) * 100 : 0;

      totalValue += currentValue;
      totalCost += fifo.totalCost;
      totalRealizedPL += fifo.realizedPL;

      const counts = countPositionsByType(token.positions);
      const dates = getTransactionDates(token.positions);

      tokenContexts.push({
        id: token.id,
        symbol: token.displayName || token.id.toUpperCase(),
        displayName: token.displayName || token.id,
        quantity: fifo.remainingQty,
        avgCost: fifo.avgCost,
        currentPrice: price,
        currentValue,
        totalCost: fifo.totalCost,
        pl: unrealizedPL,
        plPercent: Math.round(unrealizedPLPct * 100) / 100,
        allocation: 0, // Will be calculated after total is known
        buyCount: counts.buy,
        sellCount: counts.sell,
        swapCount: counts.swap,
        miningCount: counts.mining,
        stakingCount: counts.staking,
        firstTransactionDate: dates.first,
        lastTransactionDate: dates.last,
        hasTargetPrice: !!token.targetPrice,
        targetPrice: token.targetPrice,
        distanceToTarget: token.targetPrice && price > 0
          ? ((token.targetPrice - price) / price) * 100
          : undefined,
      });
    }

    // Calculate allocations
    for (const tc of tokenContexts) {
      tc.allocation =
        totalValue > 0
          ? Math.round((tc.currentValue / totalValue) * 1000) / 10
          : 0;
    }

    // Sort by allocation descending
    tokenContexts.sort((a, b) => b.allocation - a.allocation);

    // Calculate summary
    const totalPL = totalValue - totalCost + totalRealizedPL;
    const totalPLPercent =
      totalCost > 0 ? ((totalValue - totalCost) / totalCost) * 100 : 0;

    const profitableTokens = tokenContexts.filter((t) => t.pl > 0);
    const losingTokens = tokenContexts.filter((t) => t.pl < 0);

    // Find best and worst performers
    const sortedByPL = [...tokenContexts].sort((a, b) => b.plPercent - a.plPercent);
    const bestPerformer =
      sortedByPL.length > 0 && sortedByPL[0].plPercent > 0
        ? { symbol: sortedByPL[0].symbol, plPercent: sortedByPL[0].plPercent }
        : null;
    const worstPerformer =
      sortedByPL.length > 0 && sortedByPL[sortedByPL.length - 1].plPercent < 0
        ? {
            symbol: sortedByPL[sortedByPL.length - 1].symbol,
            plPercent: sortedByPL[sortedByPL.length - 1].plPercent,
          }
        : null;

    const summary: PortfolioSummary = {
      totalValue: Math.round(totalValue * 100) / 100,
      totalCost: Math.round(totalCost * 100) / 100,
      totalPL: Math.round(totalPL * 100) / 100,
      totalPLPercent: Math.round(totalPLPercent * 100) / 100,
      realizedPL: Math.round(totalRealizedPL * 100) / 100,
      unrealizedPL: Math.round((totalValue - totalCost) * 100) / 100,
      tokenCount: tokenContexts.length,
      positionCount: tokens.reduce((sum, t) => sum + t.positions.length, 0),
      profitableTokens: profitableTokens.length,
      losingTokens: losingTokens.length,
      bestPerformer,
      worstPerformer,
    };

    // Extract recent transactions
    const recentTransactions = extractRecentTransactions(tokens, 30);

    // Calculate risk metrics
    const risk = calculateRiskMetrics(tokenContexts, totalValue);

    return {
      timestamp: new Date().toISOString(),
      summary,
      tokens: tokenContexts,
      recentTransactions: recentTransactions.slice(0, 20), // Limit to 20 most recent
      risk,
      calculationMethod,
    };
  },

  /**
   * Format the context as a human-readable summary for the AI
   */
  formatForAI(context: PortfolioContext, language: 'fr' | 'en' = 'fr'): string {
    const { summary, tokens, recentTransactions, risk } = context;

    if (language === 'fr') {
      let text = `## PORTEFEUILLE DE L'UTILISATEUR\n\n`;

      // Résumé global
      text += `### Résumé Global\n`;
      text += `- Valeur totale: $${summary.totalValue.toLocaleString()}\n`;
      text += `- Coût total: $${summary.totalCost.toLocaleString()}\n`;
      text += `- P/L total: $${summary.totalPL.toLocaleString()} (${summary.totalPLPercent > 0 ? '+' : ''}${summary.totalPLPercent.toFixed(2)}%)\n`;
      text += `- P/L réalisé: $${summary.realizedPL.toLocaleString()}\n`;
      text += `- P/L non réalisé: $${summary.unrealizedPL.toLocaleString()}\n`;
      text += `- Nombre de tokens: ${summary.tokenCount}\n`;
      text += `- Nombre de positions: ${summary.positionCount}\n`;
      text += `- Tokens en profit: ${summary.profitableTokens} | En perte: ${summary.losingTokens}\n`;

      if (summary.bestPerformer) {
        text += `- Meilleur performer: ${summary.bestPerformer.symbol} (+${summary.bestPerformer.plPercent.toFixed(2)}%)\n`;
      }
      if (summary.worstPerformer) {
        text += `- Pire performer: ${summary.worstPerformer.symbol} (${summary.worstPerformer.plPercent.toFixed(2)}%)\n`;
      }

      // Risque
      text += `\n### Analyse de Risque\n`;
      text += `- Plus grande position: ${risk.largestPosition} (${risk.largestPositionPct}%)\n`;
      text += `- Concentration Top 3: ${risk.top3Concentration}%\n`;
      text += `- Risque de concentration: ${risk.concentrationRisk === 'high' ? 'Élevé' : risk.concentrationRisk === 'medium' ? 'Moyen' : 'Faible'}\n`;
      text += `- Score de diversification: ${risk.diversificationScore}/100\n`;

      // Détail par token (top 10)
      text += `\n### Détail des Tokens (Top ${Math.min(10, tokens.length)})\n`;
      for (const token of tokens.slice(0, 10)) {
        const plSign = token.pl >= 0 ? '+' : '';
        text += `\n**${token.symbol}** (${token.allocation}% du portfolio)\n`;
        text += `- Quantité: ${token.quantity.toLocaleString()}\n`;
        text += `- Prix actuel: $${token.currentPrice.toLocaleString()}\n`;
        text += `- Coût moyen: $${token.avgCost.toFixed(4)}\n`;
        text += `- Valeur: $${token.currentValue.toLocaleString()}\n`;
        text += `- P/L: ${plSign}$${token.pl.toLocaleString()} (${plSign}${token.plPercent.toFixed(2)}%)\n`;
        text += `- Transactions: ${token.buyCount} achats, ${token.sellCount} ventes, ${token.swapCount} swaps\n`;
        if (token.hasTargetPrice && token.targetPrice) {
          text += `- Target: $${token.targetPrice} (${token.distanceToTarget! > 0 ? '+' : ''}${token.distanceToTarget!.toFixed(2)}% pour atteindre)\n`;
        }
      }

      // Transactions récentes
      if (recentTransactions.length > 0) {
        text += `\n### Transactions Récentes (30 derniers jours)\n`;
        for (const tx of recentTransactions.slice(0, 10)) {
          const typeLabel = {
            buy: 'Achat',
            sell: 'Vente',
            swap: 'Swap',
            mining: 'Mining',
            staking: 'Staking',
          }[tx.type];
          text += `- ${tx.date}: ${typeLabel} ${tx.quantity} ${tx.tokenSymbol} à $${tx.price.toFixed(4)} = $${tx.total.toFixed(2)}\n`;
        }
      }

      return text;
    } else {
      // English version
      let text = `## USER'S PORTFOLIO\n\n`;

      text += `### Global Summary\n`;
      text += `- Total Value: $${summary.totalValue.toLocaleString()}\n`;
      text += `- Total Cost: $${summary.totalCost.toLocaleString()}\n`;
      text += `- Total P/L: $${summary.totalPL.toLocaleString()} (${summary.totalPLPercent > 0 ? '+' : ''}${summary.totalPLPercent.toFixed(2)}%)\n`;
      text += `- Realized P/L: $${summary.realizedPL.toLocaleString()}\n`;
      text += `- Unrealized P/L: $${summary.unrealizedPL.toLocaleString()}\n`;
      text += `- Number of tokens: ${summary.tokenCount}\n`;
      text += `- Number of positions: ${summary.positionCount}\n`;
      text += `- Profitable tokens: ${summary.profitableTokens} | Losing: ${summary.losingTokens}\n`;

      if (summary.bestPerformer) {
        text += `- Best performer: ${summary.bestPerformer.symbol} (+${summary.bestPerformer.plPercent.toFixed(2)}%)\n`;
      }
      if (summary.worstPerformer) {
        text += `- Worst performer: ${summary.worstPerformer.symbol} (${summary.worstPerformer.plPercent.toFixed(2)}%)\n`;
      }

      text += `\n### Risk Analysis\n`;
      text += `- Largest position: ${risk.largestPosition} (${risk.largestPositionPct}%)\n`;
      text += `- Top 3 concentration: ${risk.top3Concentration}%\n`;
      text += `- Concentration risk: ${risk.concentrationRisk === 'high' ? 'High' : risk.concentrationRisk === 'medium' ? 'Medium' : 'Low'}\n`;
      text += `- Diversification score: ${risk.diversificationScore}/100\n`;

      text += `\n### Token Details (Top ${Math.min(10, tokens.length)})\n`;
      for (const token of tokens.slice(0, 10)) {
        const plSign = token.pl >= 0 ? '+' : '';
        text += `\n**${token.symbol}** (${token.allocation}% of portfolio)\n`;
        text += `- Quantity: ${token.quantity.toLocaleString()}\n`;
        text += `- Current price: $${token.currentPrice.toLocaleString()}\n`;
        text += `- Avg cost: $${token.avgCost.toFixed(4)}\n`;
        text += `- Value: $${token.currentValue.toLocaleString()}\n`;
        text += `- P/L: ${plSign}$${token.pl.toLocaleString()} (${plSign}${token.plPercent.toFixed(2)}%)\n`;
        text += `- Transactions: ${token.buyCount} buys, ${token.sellCount} sells, ${token.swapCount} swaps\n`;
        if (token.hasTargetPrice && token.targetPrice) {
          text += `- Target: $${token.targetPrice} (${token.distanceToTarget! > 0 ? '+' : ''}${token.distanceToTarget!.toFixed(2)}% to reach)\n`;
        }
      }

      if (recentTransactions.length > 0) {
        text += `\n### Recent Transactions (last 30 days)\n`;
        for (const tx of recentTransactions.slice(0, 10)) {
          text += `- ${tx.date}: ${tx.type} ${tx.quantity} ${tx.tokenSymbol} at $${tx.price.toFixed(4)} = $${tx.total.toFixed(2)}\n`;
        }
      }

      return text;
    }
  },

  /**
   * Get a compact summary for quick context
   */
  getCompactSummary(context: PortfolioContext): string {
    const { summary, tokens } = context;
    const top3 = tokens.slice(0, 3).map((t) => t.symbol).join(', ');

    return `Portfolio: $${summary.totalValue.toLocaleString()} | P/L: ${summary.totalPLPercent > 0 ? '+' : ''}${summary.totalPLPercent.toFixed(2)}% | ${summary.tokenCount} tokens | Top: ${top3}`;
  },
};
